import api from './api';

export interface TenantUnit {
  id: number;
  floor: string;
  roomNumber: string;
  companyName: string;
  representativeName: string;
  representativeImageUrl?: string;
  isOccupied: boolean;
}

export interface Tenant {
  id: number;
  name: string;
  phone?: string;
  description?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  isEventContent?: boolean;
  layout: 'layout1' | 'layout2';
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvalDate?: string;
  rejectionReason?: string;
  status: 'active' | 'inactive' | 'closed';
  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
  updater?: {
    id: number;
    name: string;
    email: string;
  };
  tenantUnits?: TenantUnit[];
}

export interface TenantListResponse {
  success: boolean;
  data: {
    tenants: Tenant[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface TenantResponse {
  success: boolean;
  data: Tenant;
}

export interface CreateTenantRequest {
  name: string;
  phone?: string;
  description?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  isEventContent?: boolean;
  layout?: 'layout1' | 'layout2'; // 사용자가 선택한 레이아웃 정보
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalDate?: string;
  status?: 'active' | 'inactive' | 'closed';
}

export interface UpdateTenantRequest extends CreateTenantRequest {}

export interface TenantListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'closed';
  layout?: 'layout1' | 'layout2';
  approvalStatus?: 'pending' | 'approved' | 'rejected';
}

export interface LayoutsResponse {
  success: boolean;
  data: ('layout1' | 'layout2')[];
}

class TenantService {
  async getTenants(params?: TenantListParams): Promise<TenantListResponse> {
    const response = await api.get('/tenants', { params });
    return response.data;
  }

  async getTenant(id: number): Promise<TenantResponse> {
    const response = await api.get(`/tenants/${id}`);
    return response.data;
  }

  async createTenant(data: CreateTenantRequest): Promise<TenantResponse> {
    const response = await api.post('/tenants', data);
    return response.data;
  }

  async updateTenant(id: number, data: UpdateTenantRequest): Promise<TenantResponse> {
    const response = await api.put(`/tenants/${id}`, data);
    return response.data;
  }

  async deleteTenant(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/tenants/${id}`);
    return response.data;
  }

  async bulkDeleteTenants(ids: number[]): Promise<{ success: boolean; message: string; data: { deletedCount: number } }> {
    const response = await api.post('/tenants/bulk-delete', { ids });
    return response.data;
  }

  async getLayouts(): Promise<LayoutsResponse> {
    const response = await api.get('/tenants/layouts');
    return response.data;
  }

  async approveTenant(id: number, approvalStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string): Promise<TenantResponse> {
    const response = await api.patch(`/tenants/${id}/approve`, {
      approvalStatus,
      rejectionReason
    });
    return response.data;
  }
}

export const tenantService = new TenantService();
export default tenantService;